import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as plotcardEntitiesService from '../supabase/plotcard-entities';
import { Entity } from '../supabase/entities';
import { entityKeys } from './entities-queries';
import React from 'react';

// Cache time constants
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 10 * 60 * 1000; // 10 minutes

// Define query keys for better cache management
export const plotcardEntityKeys = {
  all: ['plotcard-entities'] as const,
  lists: () => [...plotcardEntityKeys.all, 'list'] as const,
  list: (filters: any = {}) => [...plotcardEntityKeys.lists(), filters] as const,
  byPlotcard: (plotcardId: string | number) =>
    [...plotcardEntityKeys.lists(), { plotcardId }] as const,
  byChapter: (chapterId: string | number) =>
    [...plotcardEntityKeys.lists(), { chapterId }] as const,
};

/**
 * Hook to fetch entities attached to a plotcard
 * @param plotcardId The plotcard ID to fetch entities for
 * @param options Additional options for the query
 */
export function usePlotcardEntities(
  plotcardId: string | number | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: plotcardEntityKeys.byPlotcard(plotcardId || ''),
    queryFn: async () => {
      if (!plotcardId) return [];
      return plotcardEntitiesService.getPlotcardEntities(plotcardId);
    },
    enabled: !!plotcardId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch all entities attached to plotcards in a chapter
 * @param chapterId The chapter ID to fetch entities for
 * @param options Additional options for the query
 */
export function useChapterEntities(
  chapterId: string | number | undefined,
  options: {
    enabled?: boolean,
    refetchOnMount?: boolean,
    refetchOnWindowFocus?: boolean,
    staleTime?: number
  } = {}
) {
  const queryClient = useQueryClient();

  // When chapterId changes, invalidate the previous chapter's data
  React.useEffect(() => {
    if (chapterId) {
      // Invalidate the current chapter's data to force a fresh fetch
      queryClient.invalidateQueries({
        queryKey: plotcardEntityKeys.byChapter(chapterId)
      });
    }
  }, [chapterId, queryClient]);

  return useQuery({
    queryKey: plotcardEntityKeys.byChapter(chapterId || ''),
    queryFn: async () => {
      if (!chapterId) return [];
      console.log(`Fetching entities for chapter ${chapterId}`);
      // Skip cache to ensure fresh data when the query is executed
      return plotcardEntitiesService.getChapterEntities(chapterId, true);
    },
    enabled: !!chapterId && (options.enabled !== false),
    // Always consider data stale immediately to force refetch
    staleTime: 0,
    gcTime: GC_TIME,
    // Always refetch when component mounts
    refetchOnMount: true,
    // Don't refetch on window focus to avoid unnecessary requests
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to add an entity to a plotcard
 */
export function useAddEntityToPlotcard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      plotcardId,
      entityId,
      // projectId is used in onMutate but not here
      projectId: _
    }: {
      plotcardId: string | number;
      entityId: string | number;
      projectId: string;
    }) => {
      return plotcardEntitiesService.addEntityToPlotcard(plotcardId, entityId);
    },
    onMutate: async ({ plotcardId, entityId, projectId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: plotcardEntityKeys.byPlotcard(plotcardId)
      });

      // Use the projectId from the parameters

      // Try to get the entity from the project entities cache
      const allEntities = queryClient.getQueryData<Entity[]>(
        entityKeys.byProject(projectId)
      ) || [];
      const entityToAdd = allEntities.find(e => e.id === entityId);

      if (entityToAdd) {
        // Get current entities for this plotcard
        const previousEntities = queryClient.getQueryData<Entity[]>(
          plotcardEntityKeys.byPlotcard(plotcardId)
        ) || [];

        // Update the cache with the new entity
        queryClient.setQueryData(
          plotcardEntityKeys.byPlotcard(plotcardId),
          [...previousEntities, entityToAdd]
        );
      }

      // Return the previous entities
      return {
        previousEntities: queryClient.getQueryData(plotcardEntityKeys.byPlotcard(plotcardId))
      };
    },
    onError: (_, { plotcardId }, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousEntities) {
        queryClient.setQueryData(
          plotcardEntityKeys.byPlotcard(plotcardId),
          context.previousEntities
        );
      }
    },
    onSettled: (_, __, { plotcardId }) => {
      // Only invalidate the specific plotcard entities query to ensure data consistency
      // This is a safety measure in case our optimistic update missed something
      queryClient.invalidateQueries({
        queryKey: plotcardEntityKeys.byPlotcard(plotcardId),
      });
    },
  });
}

/**
 * Hook to remove an entity from a plotcard
 */
export function useRemoveEntityFromPlotcard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      plotcardId,
      entityId,
      // projectId is not used in this function
      projectId: _
    }: {
      plotcardId: string | number;
      entityId: string | number;
      projectId?: string;
    }) => {
      await plotcardEntitiesService.removeEntityFromPlotcard(plotcardId, entityId);
      return { plotcardId, entityId };
    },
    onMutate: async ({ plotcardId, entityId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: plotcardEntityKeys.byPlotcard(plotcardId)
      });

      // Get current entities for this plotcard
      const previousEntities = queryClient.getQueryData<Entity[]>(
        plotcardEntityKeys.byPlotcard(plotcardId)
      ) || [];

      // Update the cache by removing the entity
      queryClient.setQueryData(
        plotcardEntityKeys.byPlotcard(plotcardId),
        previousEntities.filter(entity => entity.id !== entityId)
      );

      // Return the previous entities
      return { previousEntities };
    },
    onError: (_, { plotcardId }, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousEntities) {
        queryClient.setQueryData(
          plotcardEntityKeys.byPlotcard(plotcardId),
          context.previousEntities
        );
      }
    },
    onSettled: (_, __, { plotcardId }) => {
      // Only invalidate the specific plotcard entities query to ensure data consistency
      // This is a safety measure in case our optimistic update missed something
      queryClient.invalidateQueries({
        queryKey: plotcardEntityKeys.byPlotcard(plotcardId),
      });
    },
  });
}
